"use client";

import { useEffect, useState } from "react";
import { AlertTriangle, AlertOctagon, Ban, Info, CheckCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { getDegradation, type Degradation, type DegradationStage } from "@/lib/api/billing-api";
import { useStoreStore } from "@/stores/store-store";

/** Warna dan ikon per tahap. Tahap yang tidak dikenal jatuh ke gaya netral. */
const TONE: Partial<Record<DegradationStage, string>> = {
  quota_warning: "bg-amber-50 border-amber-200 text-amber-900",
  payg: "bg-blue-50 border-blue-200 text-blue-900",
  reserve: "bg-orange-50 border-orange-200 text-orange-900",
  restricted: "bg-red-50 border-red-200 text-red-800",
  suspended: "bg-red-50 border-red-300 text-red-900",
  expired: "bg-gray-50 border-gray-300 text-gray-800",
};
const DEFAULT_TONE = "bg-gray-50 border-gray-200 text-gray-800";

const ICON: Partial<Record<DegradationStage, typeof AlertTriangle>> = {
  quota_warning: AlertTriangle,
  payg: Info,
  reserve: AlertTriangle,
  restricted: AlertOctagon,
  suspended: Ban,
  expired: Ban,
};

/**
 * Banner degradasi (PF-10 bagian 4 / QC-F11).
 *
 * Backend merakit seluruh bahannya dalam satu panggilan — tahap, dampak,
 * langkah berikutnya, dan dua daftar kemampuan yang disebut terpisah sengaja:
 * "apa yang berhenti" TIDAK digabung dengan "apa yang masih jalan", karena
 * menyebut hanya yang berhenti membuat pengguna menduga sisanya ikut mati.
 *
 * Tahap `healthy` tidak merender apa pun — banner ini untuk memberi tahu ada
 * masalah, bukan untuk menegaskan semuanya baik-baik saja setiap saat.
 *
 * Daftar tahap dan kunci kemampuan **tidak disalin sebagai konstanta di
 * sini** selain untuk keperluan gaya (warna/ikon) — teksnya selalu dari
 * backend. Tahap atau kunci yang belum dikenal tetap dirender, dengan gaya
 * netral dan label mentah, supaya tahap baru dari backend tidak membuat
 * banner kosong atau rusak.
 */
export function DegradationBanner() {
  const t = useTranslations("billing.degradation");
  const { activeStoreId } = useStoreStore();
  const [data, setData] = useState<Degradation | null>(null);

  useEffect(() => {
    if (!activeStoreId) return;
    let cancelled = false;
    void (async () => {
      try {
        const d = await getDegradation(activeStoreId);
        if (!cancelled) setData(d);
      } catch {
        // Gagal membaca tahap bukan alasan menampilkan alarm — banner diam,
        // percobaan berikutnya (navigasi ke halaman lain) akan mencoba lagi.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeStoreId]);

  if (!data || data.stage === "healthy") return null;

  const tone = TONE[data.stage] ?? DEFAULT_TONE;
  const Icon = ICON[data.stage] ?? CheckCircle;

  const labelFor = (key: string) => (t.has(`capability.${key}`) ? t(`capability.${key}`) : key);

  return (
    <div data-testid="degradation-banner" data-stage={data.stage} className={`p-3 border-b text-sm ${tone}`}>
      <div className="flex items-start gap-2 max-w-screen-2xl mx-auto">
        <Icon className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">{data.headline}</p>
          <p className="text-xs">{data.impact}</p>
          <p className="text-xs font-medium">{data.nextStep}</p>

          {data.stopped.length > 0 && (
            <p data-testid="degradation-stopped" className="text-xs">
              <span className="font-medium">{t("stoppedLabel")}:</span> {data.stopped.map(labelFor).join(" · ")}
            </p>
          )}

          {data.stillWorks.length > 0 && (
            <p data-testid="degradation-still-works" className="text-xs opacity-80">
              <span className="font-medium">{t("stillWorksLabel")}:</span> {data.stillWorks.map(labelFor).join(" · ")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
